/**
 * useNodeConnectionStatus Hook
 *
 * Provides connection validation status for a workflow node.
 * Used by canvas nodes to display connection error badges.
 */

import { useMemo } from 'react';
import { useConnectionStore } from '@/stores/connectionStore';
import { isConnectionReferenceComplete } from '@/services/connectionService';
import type { ConnectionReference, ConnectionReferences } from '@/types/connectionReference';
import {
  createValidResult,
  createWarningResult,
  createMockActiveResult,
  createReferenceNotFoundResult,
  operationRequiresConnection,
  type ConnectionValidationResult,
  createMissingConnectionError,
} from '@/types/connectionValidation';
import { getBuiltInManifest } from '@/data/manifests';
import { requiresConnection } from '@/types/operationManifest';
import { useWorkflowStore } from '@/stores/workflowStore';
import { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';

type ConnectionStoreState = ReturnType<typeof useConnectionStore.getState>;
type WorkflowStoreState = ReturnType<typeof useWorkflowStore.getState>;

/**
 * Resolve a reference key against the store with a case-insensitive fallback.
 * Workflow JSON and ARM resource names sometimes drift in casing (e.g. user
 * typed displayName "ericTest" but earlier code lowercased the store key to
 * "erictest"); without this fallback those nodes show a spurious
 * "reference not found" error.
 */
function lookupReference(
  references: ConnectionReferences,
  key: string,
): ConnectionReference | undefined {
  const exact = references[key];
  if (exact) return exact;
  const lower = key.toLowerCase();
  for (const [candidateKey, ref] of Object.entries(references)) {
    if (candidateKey.toLowerCase() === lower) return ref;
  }
  return undefined;
}

const createSelectMappedReferenceKey =
  (nodeId: string) =>
  (state: ConnectionStoreState): string | null =>
    state.mapping[nodeId] ?? null;

const createSelectReference =
  (referenceKey: string | null) =>
  (state: ConnectionStoreState): ConnectionReference | undefined =>
    referenceKey ? lookupReference(state.references, referenceKey) : undefined;

const createSelectMockedNode =
  (nodeId: string) =>
  (state: WorkflowStoreState): MockNodeInfo | TriggerMockPayload | undefined =>
    state.mockedNodes[nodeId];

const isActionMock = (
  mockData: MockNodeInfo | TriggerMockPayload,
): mockData is MockNodeInfo => 'actionName' in mockData;

/**
 * Options for the useNodeConnectionStatus hook
 */
export interface UseNodeConnectionStatusOptions {
  /** Node ID in the workflow */
  nodeId: string;
  /** Operation type (e.g., 'Http', 'Agent', 'ServiceBus') */
  operationType: string;
  /** Override whether connection is required (from manifest) */
  connectionRequired?: boolean;
  /** Connection reference key from the workflow (e.g., from serviceProviderConfiguration.connectionName) */
  connectionReferenceKey?: string | undefined;
}

/**
 * Return type for the useNodeConnectionStatus hook
 */
export interface NodeConnectionStatusResult {
  /** Validation result for display */
  validationResult: ConnectionValidationResult | null;
  /** Whether the node has a connection assigned */
  hasConnection: boolean;
  /** Whether a connection is required for this node */
  isRequired: boolean;
  /** The reference key if assigned */
  referenceKey: string | null;
  /** Connection display name if available */
  connectionName: string | null;
}

/**
 * Hook to get connection validation status for a node
 *
 * @param options - Configuration options
 * @returns Connection status result
 *
 * Usage:
 * const status = useNodeConnectionStatus(options);
 * Then use status.validationResult with ConnectionStatusBadge
 */
export function useNodeConnectionStatus({
  nodeId,
  operationType,
  connectionRequired,
  connectionReferenceKey,
}: UseNodeConnectionStatusOptions): NodeConnectionStatusResult {
  // Get connection state from store
  // Prefer the passed connectionReferenceKey (from workflow data), fallback to mapping
  const mappedKeySelector = useMemo(
    () => createSelectMappedReferenceKey(nodeId),
    [nodeId],
  );
  const mappedKey = useConnectionStore(mappedKeySelector);

  // Mock data state (no longer gated by a "template mode").
  const mockedNodeSelector = useMemo(() => createSelectMockedNode(nodeId), [nodeId]);
  const nodeMockData = useWorkflowStore(mockedNodeSelector);

  // Use the explicit reference key if provided, otherwise use mapped key
  const referenceKey = connectionReferenceKey ?? mappedKey;
  const referenceSelector = useMemo(
    () => createSelectReference(referenceKey),
    [referenceKey],
  );
  const reference = useConnectionStore(referenceSelector);

  return useMemo(() => {
    // An action is "mocked" when it has canned output and its per-action
    // toggle is on (enabled !== false); a trigger is mocked whenever its entry
    // exists (the trigger payload is always sent with a mock run — no toggle).
    // The mocked status takes precedence over any connection error so the
    // badge reads "Mocked" while the node is mocked. The typeof guard skips
    // malformed sidecar entries (parseMockData doesn't validate per-entry
    // shapes, and `in` throws on primitives).
    const hasMockData = nodeMockData && typeof nodeMockData === 'object'
      ? isActionMock(nodeMockData)
        ? !!nodeMockData.mockOutput && nodeMockData.enabled !== false
        : true
      : false;
    // Determine if connection is required
    // Priority: explicit override > manifest > type-based fallback
    let isRequired = connectionRequired;
    if (isRequired === undefined) {
      const manifest = getBuiltInManifest(operationType);
      if (manifest?.properties.connection !== undefined) {
        isRequired = requiresConnection(manifest);
      } else {
        isRequired = operationRequiresConnection(operationType);
      }
    }

    // Mocked nodes win over EVERY connection status: a mocked action returns
    // its canned output instead of running live, so the badge reads "Mocked"
    // regardless of whether the connection is required, missing, incomplete,
    // or fully configured. We still resolve the reference (when present) so the
    // connector identity stays available to the canvas.
    if (hasMockData) {
      return {
        validationResult: createMockActiveResult(),
        hasConnection: reference ? isConnectionReferenceComplete(reference) : false,
        isRequired: !!isRequired,
        referenceKey: referenceKey ?? null,
        connectionName: reference?.connectionName ?? referenceKey ?? null,
      };
    }

    // If no connection required, no badge needed.
    if (!isRequired) {
      return {
        validationResult: null,
        hasConnection: false,
        isRequired: false,
        referenceKey: null,
        connectionName: null,
      };
    }

    // Check if connection is assigned
    if (!referenceKey) {
      // Global change: missing connection → warning (not error)
      return {
        validationResult: createWarningResult('Connection not yet configured'),
        hasConnection: false,
        isRequired: true,
        referenceKey: null,
        connectionName: null,
      };
    }

    // Check if reference exists in connections
    if (!reference) {
      return {
        validationResult: createReferenceNotFoundResult(referenceKey),
        hasConnection: false,
        isRequired: true,
        referenceKey,
        connectionName: null,
      };
    }

    if (!isConnectionReferenceComplete(reference)) {
      return {
        validationResult: createMissingConnectionError('Connection configuration invalid or incomplete'),
        hasConnection: false,
        isRequired: true,
        referenceKey,
        connectionName: reference.connectionName ?? referenceKey,
      };
    }

    // Connection is valid
    return {
      validationResult: createValidResult(),
      hasConnection: true,
      isRequired: true,
      referenceKey,
      connectionName: reference.connectionName ?? referenceKey,
    };
  }, [operationType, connectionRequired, referenceKey, reference, nodeMockData]);
}

/**
 * Hook to get connection validation status for multiple nodes
 * Useful for batch validation or canvas-level error display
 *
 * @param nodeIds - Array of node configurations
 * @returns Map of node ID to validation results
 */
export function useMultiNodeConnectionStatus(
  nodes: Array<{ nodeId: string; operationType: string; connectionRequired?: boolean }>
): Map<string, NodeConnectionStatusResult> {
  const mapping = useConnectionStore((state) => state.mapping);
  const references = useConnectionStore((state) => state.references);

  return useMemo(() => {
    const results = new Map<string, NodeConnectionStatusResult>();

    for (const { nodeId, operationType, connectionRequired } of nodes) {
      const isRequired = connectionRequired ?? operationRequiresConnection(operationType);

      if (!isRequired) {
        results.set(nodeId, {
          validationResult: null,
          hasConnection: false,
          isRequired: false,
          referenceKey: null,
          connectionName: null,
        });
        continue;
      }

      const referenceKey = mapping[nodeId] ?? null;

      if (!referenceKey) {
        // Global change: missing connection → warning (not error)
        results.set(nodeId, {
          validationResult: createWarningResult('Connection not yet configured'),
          hasConnection: false,
          isRequired: true,
          referenceKey: null,
          connectionName: null,
        });
        continue;
      }

      const reference = lookupReference(references, referenceKey);
      if (!reference) {
        results.set(nodeId, {
          validationResult: createReferenceNotFoundResult(referenceKey),
          hasConnection: false,
          isRequired: true,
          referenceKey,
          connectionName: null,
        });
        continue;
      }

      results.set(nodeId, {
        validationResult: createValidResult(),
        hasConnection: true,
        isRequired: true,
        referenceKey,
        connectionName: reference.connectionName ?? referenceKey,
      });
    }

    return results;
  }, [nodes, mapping, references]);
}

/**
 * Hook to check if any nodes have connection errors
 * Useful for showing workflow-level validation warnings
 */
export function useHasConnectionErrors(): boolean {
  const mapping = useConnectionStore((state) => state.mapping);
  const references = useConnectionStore((state) => state.references);

  return useMemo(() => {
    for (const [, refKey] of Object.entries(mapping)) {
      if (refKey && !references[refKey]) {
        return true; // Reference not found
      }
    }
    return false;
  }, [mapping, references]);
}

/**
 * Hook to get count of nodes with connection errors
 */
export function useConnectionErrorCount(): number {
  const mapping = useConnectionStore((state) => state.mapping);
  const references = useConnectionStore((state) => state.references);

  return useMemo(() => {
    let count = 0;
    for (const [, refKey] of Object.entries(mapping)) {
      if (refKey && !references[refKey]) {
        count++;
      }
    }
    return count;
  }, [mapping, references]);
}
